import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faExclamationCircle, faPlus, faThumbsUp, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios, { CancelTokenSource } from 'axios';
import { ActionButton, ErrorRetry, HeadingItem, SearchBox } from 'components';
import { BarcodeSearch } from 'components/barcode';
import { LineItemCard, MispicksCard } from 'components/mispicks';
import { ConfirmationPopup, MispicksPopup } from 'components/pop-up';
import { TripContext } from 'contexts';
import { List, LoadIndicator } from 'devextreme-react';
import DataSource from 'devextreme/data/data_source';
import { MispickPopupField, ScanResultType } from 'enums';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useLoadManagerService, useParamService } from 'services';
import { LicensePlate, LineItem, Mispick, OrderItem, ReturnItem } from 'services/types';
import { ScanResult } from 'types';
import './mispicks.scss';

export const MispicksPage = () => {

    const [isFetchingInventory, setIsFetchingInventory] = useState<boolean>(true)
    const [isError, setIsError] = useState<boolean>(false)
    const [isMispicksPopupAddVisible, setMispicksPopupAddVisible] = useState<boolean>(false)
    const [isConfirmationVisible, setIsConfirmationVisible] = useState<boolean>(false)
    const [isItemScanned, setIsItemScanned] = useState<boolean>(false)
    const [footerText, setFooterText] = useState<string>('')
    const [selectedMispicks, setSelectedMispicks] = useState<Mispick>({} as Mispick)
    const [isReturnStickerAvailable, setIsReturnStickerAvailable] = useState<boolean>(true)
    const [searchString, setSearchString] = useState<string>('')
    const [lineItems, setLineItems] = useState<LineItem[]>([])
    const [cancelToken, setCancelToken] = useState<CancelTokenSource>()

    const { getIntValue } = useParamService
    const { searchInventoryFetchResult } = useLoadManagerService()

    const tripId: number | undefined = getIntValue('tripId')
    const stopId: number | undefined = getIntValue('stopId')

    const tripContext = useContext(TripContext)
    const mispicksItems: Mispick[] = tripContext.mispicks.filter(m => m.stopId === stopId) ?? []

    const isValidSearch = (): boolean => searchString != null && searchString.trim().length >= 3

    useEffect(() => {
        const getData = async () => {
            try {
                const lineItems: LineItem[] = tripContext.lineItems.filter(item => item.universalProductCode == searchString) ?? []
                if (lineItems && lineItems.length > 0) {
                    setLineItems(lineItems)
                }
                else {
                    let source = axios.CancelToken.source() // Create a new CancelToken
                    if (cancelToken) {
                        cancelToken.cancel()  // Cancel the previous request before making a new request
                    }
                    setCancelToken(source)
                    let result: LineItem[] = await searchInventoryFetchResult(searchString, source)
                    if (result) {
                        setLineItems(result)
                        setCancelToken(undefined)
                    }
                    if (isItemScanned && result.length == 1) {
                        onClickCard(result[0])
                    }
                }
            }
            catch (e) {
                if (!axios.isCancel(e)) {
                    setIsError(true)
                }
            }
            finally {
                setIsFetchingInventory(false)
            }
        }

        if (isFetchingInventory) {
            setIsError(false)
            if (isValidSearch()) {
                getData()
            }
        }
    }, [searchString, isFetchingInventory])

    const onClickEdit = (id: number): void => {
        setMispicksPopupAddVisible(true)
        const index = mispicksItems.findIndex(item => item.id == id)
        setIsReturnStickerAvailable(mispicksItems[index].noReturnStickerReason == null ? true : false)
        setSelectedMispicks(mispicksItems[index])
    }

    const onClickDelete = (id: number): void => {
        setIsConfirmationVisible(true)
        const index = mispicksItems.findIndex(item => item.id == id)
        setSelectedMispicks(mispicksItems[index])
    }

    const handleConfirmation = async (): Promise<void> => {
        setIsConfirmationVisible(false)
        tripContext.mispicks.remove(selectedMispicks.id)
    }

    const onClickPopupOk = async (): Promise<void> => {
        setMispicksPopupAddVisible(false)
        setFooterText('')
        const index = mispicksItems.findIndex(item => item.id == selectedMispicks.id)
        if (index >= 0) {
            let newItems = [...mispicksItems];
            newItems[index] = selectedMispicks
            newItems[index].createdDate = newItems[index].createdDate ?? new Date()
            tripContext.mispicks.put(newItems[index])
        }
        else {
            let addedItem = { ...selectedMispicks, createdDate: new Date() }
            tripContext.mispicks.put(addedItem)
        }
    }

    const onClickAddMispick = (): void => {
        setMispicksPopupAddVisible(true)
        const newMispickItem: Mispick = {
            id: new Date().getTime(),
            stopId: stopId!,
            lineItemId: null,
            upc: null,
            quantity: null,
            createdDate: null,
            comments: null,
            returnSticker: null,
            noReturnStickerReason: null,
            mispickReason: null,
            shortedOrderItemId: null,
            longDescription: null
        }
        setIsReturnStickerAvailable(true)
        setSelectedMispicks(newMispickItem)
    }

    const onValueChanged = (mispicksField: MispickPopupField, value: any): void => {

        let newMispicksItem: Mispick = selectedMispicks
        switch (mispicksField) {
            case MispickPopupField.LineItemId:
                newMispicksItem = { ...newMispicksItem, lineItemId: +value == 0 ? null : +value }
                break;
            case MispickPopupField.Upc:
                newMispicksItem = { ...newMispicksItem, upc: value == 0 ? null : value }
                break;
            case MispickPopupField.Quantity:
                newMispicksItem = { ...newMispicksItem, quantity: value?.toString().trim().length == 0 || isNaN(value) ? null : +value }
                break;
            case MispickPopupField.ReturnSticker:
                newMispicksItem = { ...newMispicksItem, returnSticker: value == 0 ? null : +value, noReturnStickerReason: null }
                setIsReturnStickerAvailable(true)
                break;
            case MispickPopupField.NoReturnSticker:
                newMispicksItem = { ...newMispicksItem, noReturnStickerReason: value == "" ? null : value, returnSticker: null }
                setIsReturnStickerAvailable(false)
                break;
            case MispickPopupField.Comments:
                newMispicksItem = { ...newMispicksItem, comments: value }
                break;
            case MispickPopupField.ReasonCode:
                newMispicksItem = { ...newMispicksItem, mispickReason: value }
                break;
            case MispickPopupField.ShortedItemId:
                newMispicksItem = { ...newMispicksItem, shortedOrderItemId: +value }
                break;
        }
        setSelectedMispicks(newMispicksItem)
    }

    const searchInventory = async (query: string): Promise<void> => {
        setIsFetchingInventory(true)
        setSearchString(query.trim())
    }

    const onClickCard = (lineItem: LineItem): void => {
        const newMispickItem: Mispick = {
            id: new Date().getTime(),
            stopId: stopId!,
            lineItemId: lineItem.id,
            upc: lineItem.universalProductCode,
            quantity: null,
            createdDate: null,
            comments: null,
            returnSticker: null,
            noReturnStickerReason: null,
            mispickReason: null,
            shortedOrderItemId: null,
            longDescription: lineItem.longDescription
        }
        setMispicksPopupAddVisible(true)
        setSearchString('')
        setSelectedMispicks(newMispickItem)
    }

    const dataSource = new DataSource({
        store: lineItems,
        pageSize: 10,
        paginate: true
    })

    const onItemScanned = async (result: ScanResult): Promise<void> => {
        //don't respond to a scan if filling out the mispicks form
        if(isMispicksPopupAddVisible) {
            return
        }

        let queryString: string = ''
        const lineItems: LineItem[] = tripContext.lineItems.toArray() ?? []
        let lineItem: LineItem = {} as LineItem
        switch (result.type) {
            case ScanResultType.BarcodeNotFound:
                setIsItemScanned(true)
                queryString = result.values[0].value.toString()
                break;
            case ScanResultType.LineItemCurrentStop:
                setFooterText('This item belongs to the current stop')
                lineItem = lineItems.find(li => li.id == (result.values[0].value as OrderItem).lineItemId) ?? {} as LineItem
                queryString = lineItem.universalProductCode
                break;
            case ScanResultType.LineItemDifferentStop:
            case ScanResultType.LineItemDifferentLoad:
                setFooterText('Scanned item belongs to the different stop/load')
                lineItem = lineItems.find(li => li.id == (result.values[0].value as OrderItem).lineItemId) ?? {} as LineItem
                queryString = lineItem.universalProductCode
                break;
            case ScanResultType.ReturnItemCurrentStop:
                setFooterText('Scanned item belongs to the return')
                lineItem = lineItems.find(li => li.id == (result.values[0].value as ReturnItem).lineItemId) ?? {} as LineItem
                queryString = lineItem.universalProductCode
                break;
            case ScanResultType.MultipleScannedValues:
                setFooterText('Scanned item belongs to the delivery or return')
                lineItem = lineItems.find(li => li.id == (result.values[0].value as OrderItem).lineItemId) ?? {} as LineItem
                queryString = lineItem.universalProductCode
                break;
            case ScanResultType.LPCurrentStop:
                setIsItemScanned(true)
                setFooterText('Scanned a LP of current stop')
                queryString = (result.values[0].value as LicensePlate).id.toString()
                break;
            case ScanResultType.LPDifferentStop:
            case ScanResultType.LPDifferentLoad:
                setIsItemScanned(true)
                setFooterText('Scanned a LP of different stop/load')
                queryString = (result.values[0].value as LicensePlate).id.toString()
                break;
        }
        searchInventory(queryString)
    }

    const onRetry = () => {
        setIsFetchingInventory(true)
    }

    return (
        <Fragment>
            <BarcodeSearch tripId={tripId} stopId={stopId} onItemScanned={onItemScanned} onLpScanned={onItemScanned} />
            <div className={'mispick-heading-card'}>
                <div className={'item-list-heading-card-leftcoloumn'}>
                    <SearchBox placeHolder={'Search Inventory'} searchQuery={searchString} onSearch={searchInventory} />
                </div>
                <div className={'item-list-clear-card'} onClick={() => setSearchString('')}>
                    <FontAwesomeIcon icon={faTimes as IconProp} color='black' size='2x' />
                </div>
            </div>
            <div className={'mispick-listcard'}>
                {isValidSearch() ?
                    <Fragment>
                        {isError ?
                            <ErrorRetry message={'We ran into a problem while trying to search the inventory. Please ensure you have an internet connection.'} onRetry={onRetry} />
                            :
                            <Fragment>
                                {isError ?
                                    <ErrorRetry headingText='Internet Required' message={'You must be online to add a mispick. Please ensure you have an internet connection.'} onRetry={onRetry} />
                                :
                                    <Fragment>
                                        {lineItems.length > 0 && <div className={'mispick-selectany'}>Please select any from the search result</div>}
                                        <List
                                            dataSource={dataSource}
                                            itemRender={(items: LineItem) => <LineItemCard key={items.id} lineItem={items} onClickButton={onClickCard} ></LineItemCard>}
                                            noDataText={lineItems.length == 0 ? `No results for "${searchString}"` : 'No Results'}
                                            showScrollbar={'never'}
                                        />
                                        {footerText != '' &&
                                            <div className={'mispick-footer'}>
                                                <div className={`mispick-footer-icon`}>
                                                    <FontAwesomeIcon icon={faExclamationCircle as IconProp} size={'1x'} color={'#EB5757'} />
                                                </div>
                                                <div className={`mispick-footer-text`}>{footerText}</div>
                                            </div>
                                        }
                                    </Fragment>
                                }
                            </Fragment>
                        }
                    </Fragment>
                    :
                    <Fragment>
                        {mispicksItems.length == 0 ?
                                    <div className={'card-material'}>
                                        <HeadingItem text={"Add Mispick"} />
                                        <div className={'noitemtext'}>Scan an item to add it as a mispick, or search our inventory using the option above.</div>
                                        <div className={'mispick-popup-subtext'}>You can search by things like the item's description, UPC, or item number. You must be online to add a mispick.</div>
                                    </div>
                            :
                            <Fragment>
                                {
                                    <Fragment>
                                        {mispicksItems.map(items => <MispicksCard key={items.id} mispickId={items.id} onClickEdit={onClickEdit} onClickDelete={onClickDelete}></MispicksCard>)}
                                    </Fragment>
                                }
                                {/* 5/13/22 - Hid manual mispick option to see if enforcing connectivity is viable */}
                                <div style={{display: 'none'}} className={'bottom-fix-action-row'}>
                                    <ActionButton
                                        icon={faPlus}
                                        buttonColor={'#F2994A'}
                                        buttonText='Add'
                                        onClick={onClickAddMispick}
                                    />
                                </div>
                            </Fragment>
                        }
                    </Fragment>
                }
            </div>
            <MispicksPopup
                isVisible={isMispicksPopupAddVisible}
                isReturnStickerAvailable={isReturnStickerAvailable}
                mispicksItem={selectedMispicks}
                stopId={stopId}
                onClickCancel={() => setMispicksPopupAddVisible(false)}
                onValueChanged={onValueChanged}
                onClickOk={onClickPopupOk}
            />
            <ConfirmationPopup
                isVisible={isConfirmationVisible}
                title="Confirm Delete"
                message={`Are you sure you want to delete this mispick item ?`}
                button1={{ icon: faTimes }}
                button2={{ icon: faThumbsUp }}
                onClickButton1={() => setIsConfirmationVisible(false)}
                onClickButton2={handleConfirmation}
            />
        </Fragment>
    )
};
